import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { 
  UploadCloud, ChevronRight, ChevronLeft, CheckCircle, 
  User, FileText, Home, CreditCard, File, X, Loader2,
  ShieldAlert, Search, Trash2, MessageCircle, ExternalLink, Building2, Briefcase, LogOut
} from 'lucide-react';

// --- Инициализация Firebase ---
const firebaseConfig = {
  apiKey: "AIzaSyCVQOFaTHQVxBRdXZ8RNAkR0ta2wQAZXe0",
  authDomain: "abako-f26cc.firebaseapp.com",
  projectId: "abako-f26cc",
  storageBucket: "abako-f26cc.firebasestorage.app",
  messagingSenderId: "1087477025042",
  appId: "1:1087477025042:web:5ee5c311179424716e8bcb"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const DB_NAME = 'loan_applications';
/** Ключ localStorage: при несовпадении с sessionVersion в Firestore — сброс входа в админку */
const ADMIN_SESSION_STORAGE_KEY = 'abako_admin_session_v';
const APPLICANT_TYPE_INDIVIDUAL = 'persona_fisica';
const APPLICANT_TYPE_COMPANY = 'persona_giuridica';

const getApplicantType = (app) => app?.applicantType || APPLICANT_TYPE_INDIVIDUAL;
const isCompanyApplicant = (app) => getApplicantType(app) === APPLICANT_TYPE_COMPANY;

const getApplicantDisplayName = (app) => {
  if (isCompanyApplicant(app)) {
    return app.ragioneSociale || app.nomeReferente || 'Azienda senza nome';
  }
  return app.nomeCompleto || 'Cliente senza nome';
};

const INDIVIDUAL_INITIAL_DATA = {
  titolo: 'Sig.r',
  nomeCompleto: '',
  dataNascita: '',
  cittadinanza: '',
  tipoDocumento: '',
  numeroDocumento: '',
  codiceFiscale: '',
  email: '',
  indirizzo: { via: '', comune: '', citta: '', cap: '', nazione: 'Italia' },
  redditoNetto: '',
  iban: '',
  finalita: '',
  finalitaAltro: '',
  importoRichiesto: '',
  durata: '',
  durataAltro: ''
};

const COMPANY_INITIAL_DATA = {
  ragioneSociale: '',
  formaGiuridica: '',
  partitaIva: '',
  codiceFiscaleAzienda: '',
  annoCostituzione: '',
  sedeLegale: { via: '', comune: '', provincia: '', cap: '', nazione: 'Italia' },
  nomeReferente: '',
  ruoloReferente: '',
  codiceFiscaleReferente: '',
  tipoDocumentoReferente: '',
  numeroDocumentoReferente: '',
  emailAziendale: '',
  telefonoReferente: '',
  fatturatoAnnuo: '',
  utileOperativo: '',
  numeroDipendenti: '',
  esposizioneDebitoria: '',
  ibanAziendale: '',
  finalita: '',
  finalitaAltro: '',
  importoRichiesto: '',
  durata: '',
  durataAltro: ''
};

const getInitialFormData = (type) => {
  if (type === APPLICANT_TYPE_COMPANY) {
    return {
      ...COMPANY_INITIAL_DATA,
      sedeLegale: { ...COMPANY_INITIAL_DATA.sedeLegale }
    };
  }
  return {
    ...INDIVIDUAL_INITIAL_DATA,
    indirizzo: { ...INDIVIDUAL_INITIAL_DATA.indirizzo }
  };
};

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Роутинг на основе хэша (/#admin)
  const [currentPath, setCurrentPath] = useState(window.location.hash);

  useEffect(() => {
    const handleHashChange = () => setCurrentPath(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        await signInAnonymously(auth);
      } catch (error) {
        console.error("Auth error:", error);
      }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isAdminRoute = currentPath === '#admin';

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col">
      {/* Шапка сайта */}
      <header className="bg-white shadow-sm sticky top-0 z-10 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => window.location.hash = ''}
          >
            {/* ТВОЙ ЛОГОТИП (Файл logo.png должен лежать в папке public) */}
            <img 
              src="/logo.png" 
              alt="Abako Credit" 
              className="h-8 w-auto object-contain"
              onError={(e) => {
                // Если картинка не найдена, показываем синий квадрат как раньше
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }} 
            />
            <div className="w-8 h-8 bg-blue-600 rounded-lg items-center justify-center text-white font-bold text-xl" style={{display: 'none'}}>
              A
            </div>
            
            {/* Название рядом с логотипом */}
            <span className="font-bold text-xl tracking-tight text-gray-900">ABAKO S.r.l.</span>
          </div>
        </div>
      </header>

      {/* Основной контент */}
      <main className="max-w-5xl mx-auto py-8 px-4 flex-grow w-full">
        {isAdminRoute ? <AdminWrapper user={user} /> : <ClientForm user={user} />}
      </main>

      {/* Юридический футер */}
      <footer className="mt-auto py-8 bg-gray-100 text-center text-xs text-gray-500 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4">
          <p className="font-medium text-gray-700 text-sm">© 2026 ABAKO S.r.l. Tutti i diritti riservati.</p>
          <p className="mt-1">Sede Legale: STRADA STATALE 16 ADRIATICA 8, 61121 PESARO (PU) | P.IVA: 02777020419</p>
          <p className="mt-4 text-[10px] max-w-4xl mx-auto text-gray-400 leading-relaxed">
            L'erogazione del finanziamento è soggetta alla valutazione e all'approvazione da parte dell'istituto erogante. 
            Questa piattaforma utilizza protocolli di crittografia avanzata a 256-bit (SSL/TLS) per garantire la massima sicurezza 
            dei tuoi dati personali e finanziari. Nessun dato sensibile viene condiviso con terze parti non autorizzate.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ==========================================
// ОБЕРТКА ДЛЯ АДМИНКИ (АВТОРИЗАЦИЯ)
// ==========================================
function AdminWrapper({ user }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [serverSessionVersion, setServerSessionVersion] = useState(null);

  // ПАРОЛЬ ДЛЯ ВХОДА В АДМИНКУ
  const ADMIN_PASSWORD = "AbakoTop1!"; 

  // Версия сессии админки: документ Firestore `admin_config/settings` поле `sessionVersion` (number).
  // Увеличьте sessionVersion в консоли Firebase — все открытые админки сбросятся.
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'admin_config', 'settings');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const raw = snap.exists() ? snap.data().sessionVersion : undefined;
        const v = typeof raw === 'number' && Number.isFinite(raw) ? raw : 1;
        setServerSessionVersion(v);
      },
      (err) => {
        console.error('admin_config/settings:', err);
        setServerSessionVersion(1);
      }
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (serverSessionVersion === null) return;
    if (!isAuthenticated) return;
    const stored = localStorage.getItem(ADMIN_SESSION_STORAGE_KEY);
    if (stored === null) {
      localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, String(serverSessionVersion));
      return;
    }
    if (String(serverSessionVersion) !== stored) {
      localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
      setIsAuthenticated(false);
    }
  }, [isAuthenticated, serverSessionVersion]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      const v = serverSessionVersion ?? 1;
      localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, String(v));
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Неверный пароль. Попробуйте еще раз.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
    setIsAuthenticated(false);
    setPassword('');
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-2xl shadow-sm border border-gray-100 animate-fadeIn">
        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldAlert className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Панель управления</h2>
        <p className="text-center text-gray-500 mb-8 text-sm">Введите пароль для доступа к заявкам</p>
        
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Пароль"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          <button 
            type="submit"
            className="w-full py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            Войти
          </button>
        </form>
      </div>
    );
  }

  return <AdminPanel user={user} onLogout={handleLogout} />;
}

// ==========================================
// КОМПОНЕНТ АДМИН ПАНЕЛИ (НА РУССКОМ)
// ==========================================
function AdminPanel({ user, onLogout }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user) return;
    
    const appsRef = collection(db, DB_NAME);
    
    const unsubscribe = onSnapshot(appsRef, 
      (snapshot) => {
        const appsData = [];
        snapshot.forEach((doc) => {
          appsData.push({ id: doc.id, ...doc.data() });
        });
        appsData.sort((a, b) => {
          const timeA = a.timestamp?.seconds || 0;
          const timeB = b.timestamp?.seconds || 0;
          return timeB - timeA;
        });
        setApplications(appsData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching applications:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Функция для форматирования времени по Москве (до минуты)
  const formatTimeMSK = (timestamp) => {
    if (!timestamp) return 'Неизвестно';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Неизвестно';

    return date.toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) + ' МСК';
  };

  // Функция удаления заявки
  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    const isConfirmed = window.confirm("Вы уверены, что хотите удалить эту заявку? Это действие нельзя отменить.");
    if (isConfirmed) {
      try {
        await deleteDoc(doc(db, DB_NAME, id));
        if (selectedApp?.id === id) {
          setSelectedApp(null);
        }
      } catch (error) {
        console.error("Ошибка при удалении:", error);
        alert("Не удалось удалить заявку.");
      }
    }
  };

  const getTypeLabel = (app) => (
    isCompanyApplicant(app) ? 'Юр. лицо' : 'Физ. лицо'
  );

  // Фильтрация по имени компании / клиента
  const filteredApps = applications.filter(app => {
    const keyword = searchTerm.toLowerCase();
    return (
      (app.nomeCompleto || '').toLowerCase().includes(keyword) ||
      (app.ragioneSociale || '').toLowerCase().includes(keyword) ||
      (app.nomeReferente || '').toLowerCase().includes(keyword)
    );
  });

  if (loading) {
    return <div className="text-center py-20 text-gray-500 flex flex-col items-center justify-center"><Loader2 className="w-8 h-8 animate-spin mb-4 text-blue-500" /> Загрузка заявок...</div>;
  }

  return (
    <div className="animate-fadeIn">
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={onLogout}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
        >
          <LogOut size={16} />
          Выйти
        </button>
      </div>
      {/* Шапка админки с поиском */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <h1 className="text-3xl font-bold text-gray-900 w-full sm:w-auto text-center sm:text-left">Входящие заявки</h1>
        
        {!selectedApp && (
          <div className="flex w-full sm:w-auto items-center gap-4">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                placeholder="Поиск по ФИО или компании..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
              />
            </div>
            <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap">
              Всего: {filteredApps.length}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {selectedApp ? (
          <div className="p-0">
            <div className="bg-gray-50 p-6 border-b flex justify-between items-center flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  Клиент: {getApplicantDisplayName(selectedApp)}
                  <span className={`text-xs px-2 py-1 rounded-full border ${
                    isCompanyApplicant(selectedApp)
                      ? 'bg-violet-50 text-violet-700 border-violet-200'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}>
                    {getTypeLabel(selectedApp)}
                  </span>
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Дата отправки: {formatTimeMSK(selectedApp.timestamp)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => handleDelete(selectedApp.id)}
                  className="px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-2"
                >
                  <Trash2 size={16} /> Удалить
                </button>
                <button 
                  onClick={() => setSelectedApp(null)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <ChevronLeft size={16} /> Назад к списку
                </button>
              </div>
            </div>
            
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              {isCompanyApplicant(selectedApp) ? (
                <>
                  <div>
                    <h3 className="font-semibold text-violet-900 border-b pb-2 mb-4 uppercase text-xs tracking-wider">Dati azienda</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Ragione sociale:</dt> <dd className="font-medium text-right">{selectedApp.ragioneSociale}</dd></div>
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Forma giuridica:</dt> <dd className="font-medium text-right">{selectedApp.formaGiuridica}</dd></div>
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Partita IVA:</dt> <dd className="font-medium uppercase text-right">{selectedApp.partitaIva}</dd></div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="font-semibold text-violet-900 border-b pb-2 mb-4 uppercase text-xs tracking-wider">Referente e contatti</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Referente:</dt> <dd className="font-medium text-right">{selectedApp.nomeReferente}</dd></div>
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Ruolo:</dt> <dd className="font-medium text-right">{selectedApp.ruoloReferente}</dd></div>
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Email aziendale:</dt> <dd className="font-medium text-right"><a href={`mailto:${selectedApp.emailAziendale}`} className="text-blue-600 hover:underline">{selectedApp.emailAziendale}</a></dd></div>
                      <div className="flex justify-between gap-6"><dt className="text-gray-500">Telefono:</dt> <dd className="font-medium text-right">{selectedApp.telefonoReferente}</dd></div>
                    </dl>
                  </div>

                  <div className="md:col-span-2">
                    <h3 className="font-semibold text-violet-900 border-b pb-2 mb-4 uppercase text-xs tracking-wider">Dati finanziamento aziendale</h3>
                    <div className="bg-violet-50 p-6 rounded-xl grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <span className="block text-xs text-violet-600 mb-1">Fatturato annuo</span>
                        <span className="text-lg font-bold text-violet-900">€ {selectedApp.fatturatoAnnuo}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-violet-600 mb-1">Importo richiesto</span>
                        <span className="text-lg font-bold text-violet-900">€ {selectedApp.importoRichiesto}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-violet-600 mb-1">Durata</span>
                        <span className="text-lg font-bold text-violet-900">{selectedApp.durata === 'Altro' ? selectedApp.durataAltro : selectedApp.durata} mesi</span>
                      </div>
                      <div>
                        <span className="block text-xs text-violet-600 mb-1">Finalità</span>
                        <span className="text-sm font-bold text-violet-900">{selectedApp.finalita === 'Altro' ? selectedApp.finalitaAltro : selectedApp.finalita}</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-violet-100 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">IBAN aziendale:</span><span className="font-mono font-medium tracking-widest">{selectedApp.ibanAziendale}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Dipendenti:</span><span className="font-medium">{selectedApp.numeroDipendenti}</span></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3 className="font-semibold text-blue-900 border-b pb-2 mb-4 uppercase text-xs tracking-wider">Личные данные</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between"><dt className="text-gray-500">Дата рождения:</dt> <dd className="font-medium">{selectedApp.dataNascita}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Гражданство:</dt> <dd className="font-medium">{selectedApp.cittadinanza}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Codice Fiscale:</dt> <dd className="font-medium uppercase">{selectedApp.codiceFiscale}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Документ:</dt> <dd className="font-medium">{selectedApp.tipoDocumento} - {selectedApp.numeroDocumento}</dd></div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="font-semibold text-blue-900 border-b pb-2 mb-4 uppercase text-xs tracking-wider">Контакты и Адрес</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between"><dt className="text-gray-500">Email:</dt> <dd className="font-medium"><a href={`mailto:${selectedApp.email}`} className="text-blue-600 hover:underline">{selectedApp.email}</a></dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Улица:</dt> <dd className="font-medium">{selectedApp.indirizzo?.via}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Коммуна/Город:</dt> <dd className="font-medium">{selectedApp.indirizzo?.comune} ({selectedApp.indirizzo?.citta})</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Индекс/Страна:</dt> <dd className="font-medium">{selectedApp.indirizzo?.cap}, {selectedApp.indirizzo?.nazione}</dd></div>
                    </dl>
                  </div>

                  <div className="md:col-span-2">
                    <h3 className="font-semibold text-blue-900 border-b pb-2 mb-4 uppercase text-xs tracking-wider">Параметры кредита</h3>
                    <div className="bg-blue-50 p-6 rounded-xl grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <span className="block text-xs text-blue-600 mb-1">Чистый доход (мес)</span>
                        <span className="text-lg font-bold text-blue-900">€ {selectedApp.redditoNetto}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-blue-600 mb-1">Запрашиваемая сумма</span>
                        <span className="text-lg font-bold text-blue-900">€ {selectedApp.importoRichiesto}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-blue-600 mb-1">Срок</span>
                        <span className="text-lg font-bold text-blue-900">{selectedApp.durata === 'Altro' ? selectedApp.durataAltro : selectedApp.durata} мес.</span>
                      </div>
                      <div>
                        <span className="block text-xs text-blue-600 mb-1">Цель</span>
                        <span className="text-sm font-bold text-blue-900">{selectedApp.finalita === 'Altro' ? selectedApp.finalitaAltro : selectedApp.finalita}</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-blue-100 flex justify-between items-center text-sm">
                       <span className="text-gray-500">IBAN:</span>
                       <span className="font-mono font-medium tracking-widest">{selectedApp.iban}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          // ТАБЛИЦА ВСЕХ ЗАЯВОК
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 text-gray-700 font-medium uppercase text-xs border-b tracking-wider">
                <tr>
                  <th className="px-6 py-4">Дата отправки</th>
                  <th className="px-6 py-4">Тип</th>
                  <th className="px-6 py-4">Клиент</th>
                  <th className="px-6 py-4">Email</th>
                  <th className="px-6 py-4">Сумма</th>
                  <th className="px-6 py-4 text-right">Действие</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      {searchTerm ? 'Заявок с таким именем не найдено.' : 'Пока нет ни одной заявки.'}
                    </td>
                  </tr>
                )}
                {filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-blue-50/50 transition-colors cursor-pointer group" onClick={() => setSelectedApp(app)}>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {formatTimeMSK(app.timestamp)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full border ${
                        isCompanyApplicant(app)
                          ? 'bg-violet-50 text-violet-700 border-violet-200'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>
                        {getTypeLabel(app)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900 flex items-center gap-2">
                      {getApplicantDisplayName(app)}
                    </td>
                    <td className="px-6 py-4 text-gray-500">{app.email || app.emailAziendale}</td>
                    <td className="px-6 py-4 font-bold text-blue-600">€ {app.importoRichiesto}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end items-center gap-4">
                        <button 
                          onClick={(e) => handleDelete(app.id, e)} 
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Удалить заявку"
                        >
                          <Trash2 size={18} />
                        </button>
                        <button className="text-blue-600 group-hover:text-blue-800 font-medium text-sm flex items-center gap-1">
                          Открыть <ChevronRight size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// КОМПОНЕНТ КЛИЕНТСКОЙ АНКЕТЫ (НА ИТАЛЬЯНСКОМ)
// ==========================================
function ClientForm({ user }) {
  const [applicantType, setApplicantType] = useState(null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Физические файлы для отображения только в UI клиента
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [formData, setFormData] = useState(getInitialFormData(APPLICANT_TYPE_INDIVIDUAL));

  useEffect(() => {
    if (!applicantType) return;
    setStep(1);
    setIsSuccess(false);
    setSelectedFiles([]);
    setFormData(getInitialFormData(applicantType));
  }, [applicantType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => file.size <= 20 * 1024 * 1024);
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
    
    if (validFiles.length < newFiles.length) {
      alert("Alcuni file superano il limite di 20 MB e non sono stati aggiunti.");
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(prev => prev + 1);
  };
  
  const prevStep = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      // Сохранение заявки в базу данных (Firestore)
      const dbCollection = collection(db, DB_NAME);
      const payload = {
        ...formData,
        applicantType,
        timestamp: serverTimestamp(),
        status: 'Nuova'
      };

      if (applicantType === APPLICANT_TYPE_COMPANY) {
        payload.email = formData.emailAziendale;
        payload.nomeCompleto = formData.nomeReferente;
      }

      await addDoc(dbCollection, {
        ...payload
      });

      setIsSuccess(true);
    } catch (error) {
      console.error("Error:", error);
      alert("Si è verificato un errore durante l'invio. Riprova più tardi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompanyFlow = applicantType === APPLICANT_TYPE_COMPANY;

  if (isSuccess) {
    return (
      <div className="bg-white rounded-3xl shadow-sm p-8 sm:p-14 text-center max-w-3xl mx-auto border border-gray-100 animate-fadeIn">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-green-600 w-10 h-10" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Richiesta inviata con successo!</h2>
        
        <div className="bg-gray-50 py-4 px-6 rounded-xl inline-block mb-6 border border-gray-200">
          <p className="text-gray-500 text-sm mb-1 uppercase tracking-widest font-semibold">Numero della pratica</p>
          <p className="text-2xl font-bold text-blue-600 tracking-wider">CPR-2690497</p>
        </div>

        <p className="text-gray-600 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
          Grazie per aver scelto <strong>ABAKO S.r.l.</strong> Abbiamo ricevuto i tuoi dati. 
          Puoi seguire lo stato della tua pratica nella tua Area Riservata o contattare direttamente il tuo manager dedicato.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a 
            href="https://t.me/abako_srl" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#0088cc] text-white px-8 py-4 rounded-2xl font-medium hover:bg-[#0077b3] transition-colors shadow-md shadow-blue-200/50"
          >
            <MessageCircle size={20} />
            Manager su Telegram
          </a>
          <a 
            href="https://areariservata.abako.credit" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-2xl font-medium hover:bg-gray-800 transition-colors shadow-md shadow-gray-200/50"
          >
            <ExternalLink size={20} />
            Vai all'Area Riservata
          </a>
        </div>
      </div>
    );
  }

  const steps = isCompanyFlow
    ? [
        { id: 1, title: 'Dati Azienda', icon: <Building2 size={20} /> },
        { id: 2, title: 'Referente', icon: <User size={20} /> },
        { id: 3, title: 'Contatti', icon: <Home size={20} /> },
        { id: 4, title: 'Finanziamento', icon: <Briefcase size={20} /> },
        { id: 5, title: 'Allegati', icon: <UploadCloud size={20} /> }
      ]
    : [
        { id: 1, title: 'Dati Personali', icon: <User size={20} /> },
        { id: 2, title: 'Documenti', icon: <FileText size={20} /> },
        { id: 3, title: 'Residenza', icon: <Home size={20} /> },
        { id: 4, title: 'Prestito', icon: <CreditCard size={20} /> },
        { id: 5, title: 'Allegati', icon: <UploadCloud size={20} /> }
      ];

  if (!applicantType) {
    return (
      <div className="max-w-4xl mx-auto animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-2">
            Chi richiede il finanziamento?
          </h2>
          <p className="text-center text-gray-500 mb-8">
            Seleziona il profilo per compilare il questionario corretto.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <button
              type="button"
              onClick={() => setApplicantType(APPLICANT_TYPE_INDIVIDUAL)}
              className="border border-blue-100 bg-blue-50 hover:bg-blue-100 rounded-2xl p-8 sm:p-10 text-left transition-colors"
            >
              <User className="w-12 h-12 text-blue-700 mb-4" />
              <p className="text-xl font-bold text-blue-900 mb-1">Persona fisica</p>
              <p className="text-sm text-blue-700">
                Per lavoratori dipendenti, autonomi e privati.
              </p>
            </button>
            <button
              type="button"
              onClick={() => setApplicantType(APPLICANT_TYPE_COMPANY)}
              className="border border-violet-100 bg-violet-50 hover:bg-violet-100 rounded-2xl p-8 sm:p-10 text-left transition-colors"
            >
              <Building2 className="w-12 h-12 text-violet-700 mb-4" />
              <p className="text-xl font-bold text-violet-900 mb-1">Persona giuridica</p>
              <p className="text-sm text-violet-700">
                Per SRL, SPA, SNC e altre imprese registrate in Italia.
              </p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Stepper */}
      <div className="mb-8">
        <div className="flex justify-between items-center relative">
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-200 -z-10 rounded-full"></div>
          <div 
            className="absolute left-0 top-1/2 transform -translate-y-1/2 h-1 bg-blue-600 -z-10 rounded-full transition-all duration-300" 
            style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
          ></div>
          
          {steps.map((s) => (
            <div key={s.id} className="flex flex-col items-center gap-2 bg-gray-50 px-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                step >= s.id ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : 'bg-white border-gray-300 text-gray-400'
              }`}>
                {s.icon}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${step >= s.id ? 'text-blue-600' : 'text-gray-400'}`}>
                {s.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-4">
            {steps[step-1].title}
          </h2>

          <form onSubmit={step === 5 ? handleSubmit : (e) => { e.preventDefault(); nextStep(); }}>
            {!isCompanyFlow && step === 1 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex gap-4">
                  <div className="w-1/3 sm:w-1/4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titolo</label>
                    <select 
                      name="titolo" value={formData.titolo} onChange={handleInputChange}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                    >
                      <option value="Sig.r">Sig.r</option>
                      <option value="Sig.ra">Sig.ra</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                    <input 
                      type="text" name="nomeCompleto" required value={formData.nomeCompleto} onChange={handleInputChange}
                      placeholder="Mario Rossi"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Data di nascita</label>
                    <input 
                      type="date" name="dataNascita" required value={formData.dataNascita} onChange={handleInputChange}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cittadinanza</label>
                    <input 
                      type="text" name="cittadinanza" required value={formData.cittadinanza} onChange={handleInputChange}
                      placeholder="Es. Italiana"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                    />
                  </div>
                </div>
              </div>
            )}

            {!isCompanyFlow && step === 2 && (
              <div className="space-y-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">Tipo di Documento d’identità</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {['Patente di guida', 'Passaporto', 'Carta d\'identità UE'].map((doc) => (
                      <label key={doc} className={`cursor-pointer border rounded-xl p-4 flex items-center gap-3 transition-colors ${formData.tipoDocumento === doc ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}>
                        <input 
                          type="radio" name="tipoDocumento" value={doc} required
                          checked={formData.tipoDocumento === doc} onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-800">{doc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Numero del documento</label>
                    <input 
                      type="text" name="numeroDocumento" required value={formData.numeroDocumento} onChange={handleInputChange}
                      placeholder="AB1234567"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Codice fiscale</label>
                    <input 
                      type="text" name="codiceFiscale" required value={formData.codiceFiscale} onChange={handleInputChange}
                      placeholder="RSSMRA..."
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase transition-shadow"
                    />
                  </div>
                </div>
              </div>
            )}

            {!isCompanyFlow && step === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
                  <input 
                    type="email" name="email" required value={formData.email} onChange={handleInputChange}
                    placeholder="mario.rossi@email.com"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                  />
                </div>

                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-4">
                  <h3 className="font-medium text-gray-900 mb-2">Indirizzo di residenza</h3>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Via e numero civico</label>
                    <input 
                      type="text" name="indirizzo.via" required value={formData.indirizzo.via} onChange={handleInputChange}
                      placeholder="Via Roma, 10"
                      className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Comune</label>
                      <input 
                        type="text" name="indirizzo.comune" required value={formData.indirizzo.comune} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Città</label>
                      <input 
                        type="text" name="indirizzo.citta" required value={formData.indirizzo.citta} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">CAP</label>
                      <input 
                        type="text" name="indirizzo.cap" required value={formData.indirizzo.cap} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Nazione</label>
                      <input 
                        type="text" name="indirizzo.nazione" required value={formData.indirizzo.nazione} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isCompanyFlow && step === 4 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reddito netto mensile (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                      <input 
                        type="number" name="redditoNetto" required value={formData.redditoNetto} onChange={handleInputChange}
                        placeholder="2000" min="0"
                        className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Importo netto richiesto (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                      <input 
                        type="number" name="importoRichiesto" required value={formData.importoRichiesto} onChange={handleInputChange}
                        placeholder="10000" min="0"
                        className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                  <input 
                    type="text" name="iban" required value={formData.iban} onChange={handleInputChange}
                    placeholder="IT00A0000000000000000000000"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase transition-shadow"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Finalità del prestito</label>
                    <div className="space-y-2">
                      {['Acquisto Auto/Moto', 'Ristrutturazione Casa', 'Liquidità', 'Spese Mediche', 'Altro'].map((fin) => (
                        <label key={fin} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input 
                            type="radio" name="finalita" value={fin} required
                            checked={formData.finalita === fin} onChange={handleInputChange}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm text-gray-800">{fin}</span>
                        </label>
                      ))}
                      {formData.finalita === 'Altro' && (
                        <input 
                          type="text" name="finalitaAltro" required placeholder="Specifica finalità..." value={formData.finalitaAltro} onChange={handleInputChange}
                          className="w-full mt-2 p-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Durata (mesi)</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['12', '24', '36', '48', '60', '72', '84', 'Altro'].map((dur) => (
                        <label key={dur} className={`cursor-pointer border text-center rounded-lg p-2 transition-colors ${formData.durata === dur ? 'border-blue-500 bg-blue-50 font-medium text-blue-700' : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'}`}>
                          <input 
                            type="radio" name="durata" value={dur} required
                            checked={formData.durata === dur} onChange={handleInputChange}
                            className="hidden"
                          />
                          <span className="text-sm">{dur}</span>
                        </label>
                      ))}
                    </div>
                    {formData.durata === 'Altro' && (
                      <input 
                        type="number" name="durataAltro" required placeholder="Inserisci mesi..." value={formData.durataAltro} onChange={handleInputChange}
                        className="w-full mt-3 p-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {!isCompanyFlow && step === 5 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
                  <p className="text-sm text-blue-800 leading-relaxed">
                    Si prega di allegare copia del documento di riconoscimento, codice fiscale e ultimo documento di reddito (busta paga o dichiarazione dei redditi).
                    <br /><strong>Dimensione massima: 20 MB per file.</strong>
                  </p>
                </div>

                <div 
                  className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current.click()}
                >
                  <UploadCloud className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Clicca qui per caricare i file</h3>
                  <p className="text-sm text-gray-500">oppure trascinali in quest'area (PDF, JPG, PNG)</p>
                  <input 
                    type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange}
                  />
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <h4 className="font-medium text-gray-900">File allegati:</h4>
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl shadow-sm text-sm font-bold text-gray-700">
                        <div className="flex items-center gap-2 overflow-hidden pr-4">
                          <File size={16} className="text-blue-500 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                        </div>
                        <button type="button" onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500 p-1">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isCompanyFlow && step === 1 && (
              <div className="space-y-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ragione sociale</label>
                  <input
                    type="text" name="ragioneSociale" required value={formData.ragioneSociale} onChange={handleInputChange}
                    placeholder="ABAKO S.r.l."
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Forma giuridica</label>
                    <select
                      name="formaGiuridica" required value={formData.formaGiuridica} onChange={handleInputChange}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    >
                      <option value="">Seleziona</option>
                      <option value="SRL">SRL</option>
                      <option value="SPA">SPA</option>
                      <option value="SNC">SNC</option>
                      <option value="SAS">SAS</option>
                      <option value="Ditta individuale">Ditta individuale</option>
                      <option value="Altro">Altro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Anno di costituzione</label>
                    <input
                      type="number" name="annoCostituzione" required value={formData.annoCostituzione} onChange={handleInputChange}
                      min="1900" max="2100" placeholder="2018"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Partita IVA</label>
                    <input
                      type="text" name="partitaIva" required value={formData.partitaIva} onChange={handleInputChange}
                      placeholder="01234567890"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none uppercase transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Codice fiscale azienda</label>
                    <input
                      type="text" name="codiceFiscaleAzienda" value={formData.codiceFiscaleAzienda} onChange={handleInputChange}
                      placeholder="Facoltativo"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none uppercase transition-shadow"
                    />
                  </div>
                </div>
              </div>
            )}

            {isCompanyFlow && step === 2 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome e cognome referente</label>
                    <input
                      type="text" name="nomeReferente" required value={formData.nomeReferente} onChange={handleInputChange}
                      placeholder="Mario Rossi"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ruolo in azienda</label>
                    <input
                      type="text" name="ruoloReferente" required value={formData.ruoloReferente} onChange={handleInputChange}
                      placeholder="Amministratore unico"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Codice fiscale referente</label>
                  <input
                    type="text" name="codiceFiscaleReferente" required value={formData.codiceFiscaleReferente} onChange={handleInputChange}
                    placeholder="RSSMRA80A01H501U"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none uppercase transition-shadow"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo documento referente</label>
                    <select
                      name="tipoDocumentoReferente" required value={formData.tipoDocumentoReferente} onChange={handleInputChange}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    >
                      <option value="">Seleziona</option>
                      <option value="Carta d'identità">Carta d'identità</option>
                      <option value="Passaporto">Passaporto</option>
                      <option value="Patente">Patente</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Numero documento</label>
                    <input
                      type="text" name="numeroDocumentoReferente" required value={formData.numeroDocumentoReferente} onChange={handleInputChange}
                      placeholder="AB1234567"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none uppercase transition-shadow"
                    />
                  </div>
                </div>
              </div>
            )}

            {isCompanyFlow && step === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email aziendale</label>
                    <input
                      type="email" name="emailAziendale" required value={formData.emailAziendale} onChange={handleInputChange}
                      placeholder="amministrazione@azienda.it"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefono referente</label>
                  <input
                    type="tel" name="telefonoReferente" required value={formData.telefonoReferente} onChange={handleInputChange}
                    placeholder="+39 333 123 4567"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                  />
                </div>
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-4">
                  <h3 className="font-medium text-gray-900 mb-2">Sede legale</h3>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Via e numero civico</label>
                    <input
                      type="text" name="sedeLegale.via" required value={formData.sedeLegale.via} onChange={handleInputChange}
                      placeholder="Via Roma, 10"
                      className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Comune</label>
                      <input
                        type="text" name="sedeLegale.comune" required value={formData.sedeLegale.comune} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Provincia</label>
                      <input
                        type="text" name="sedeLegale.provincia" required value={formData.sedeLegale.provincia} onChange={handleInputChange}
                        placeholder="PU"
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none uppercase transition-shadow"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">CAP</label>
                      <input
                        type="text" name="sedeLegale.cap" required value={formData.sedeLegale.cap} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Nazione</label>
                      <input
                        type="text" name="sedeLegale.nazione" required value={formData.sedeLegale.nazione} onChange={handleInputChange}
                        className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isCompanyFlow && step === 4 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fatturato annuo (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                      <input
                        type="number" name="fatturatoAnnuo" required value={formData.fatturatoAnnuo} onChange={handleInputChange}
                        min="0" placeholder="500000"
                        className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Utile operativo annuo (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                      <input
                        type="number" name="utileOperativo" required value={formData.utileOperativo} onChange={handleInputChange}
                        min="0" placeholder="70000"
                        className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Numero dipendenti</label>
                    <input
                      type="number" name="numeroDipendenti" required value={formData.numeroDipendenti} onChange={handleInputChange}
                      min="0" placeholder="12"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Esposizione debitoria residua (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                      <input
                        type="number" name="esposizioneDebitoria" required value={formData.esposizioneDebitoria} onChange={handleInputChange}
                        min="0" placeholder="120000"
                        className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Importo richiesto (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                      <input
                        type="number" name="importoRichiesto" required value={formData.importoRichiesto} onChange={handleInputChange}
                        min="0" placeholder="100000"
                        className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IBAN aziendale</label>
                    <input
                      type="text" name="ibanAziendale" required value={formData.ibanAziendale} onChange={handleInputChange}
                      placeholder="IT00A0000000000000000000000"
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none uppercase transition-shadow"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Finalità del finanziamento</label>
                    <div className="space-y-2">
                      {['Capitale circolante', 'Acquisto macchinari', 'Investimenti digitali', 'Liquidità aziendale', 'Altro'].map((fin) => (
                        <label key={fin} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio" name="finalita" value={fin} required
                            checked={formData.finalita === fin} onChange={handleInputChange}
                            className="w-4 h-4 text-violet-600"
                          />
                          <span className="text-sm text-gray-800">{fin}</span>
                        </label>
                      ))}
                      {formData.finalita === 'Altro' && (
                        <input
                          type="text" name="finalitaAltro" required placeholder="Specifica finalità..." value={formData.finalitaAltro} onChange={handleInputChange}
                          className="w-full mt-2 p-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-violet-500"
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Durata (mesi)</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['12', '24', '36', '48', '60', '72', '84', 'Altro'].map((dur) => (
                        <label key={dur} className={`cursor-pointer border text-center rounded-lg p-2 transition-colors ${formData.durata === dur ? 'border-violet-500 bg-violet-50 font-medium text-violet-700' : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'}`}>
                          <input
                            type="radio" name="durata" value={dur} required
                            checked={formData.durata === dur} onChange={handleInputChange}
                            className="hidden"
                          />
                          <span className="text-sm">{dur}</span>
                        </label>
                      ))}
                    </div>
                    {formData.durata === 'Altro' && (
                      <input
                        type="number" name="durataAltro" required placeholder="Inserisci mesi..." value={formData.durataAltro} onChange={handleInputChange}
                        className="w-full mt-3 p-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-violet-500"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {isCompanyFlow && step === 5 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 mb-6">
                  <p className="text-sm text-violet-800 leading-relaxed">
                    Allega i documenti principali richiesti per l'istruttoria aziendale: visura camerale aggiornata,
                    ultimo bilancio depositato e documento d'identità del legale rappresentante.
                    <br /><strong>Dimensione massima: 20 MB per file.</strong>
                  </p>
                </div>

                <div
                  className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current.click()}
                >
                  <UploadCloud className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Clicca qui per caricare i file</h3>
                  <p className="text-sm text-gray-500">oppure trascinali in quest'area (PDF, JPG, PNG)</p>
                  <input
                    type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange}
                  />
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <h4 className="font-medium text-gray-900">File allegati:</h4>
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl shadow-sm text-sm font-bold text-gray-700">
                        <div className="flex items-center gap-2 overflow-hidden pr-4">
                          <File size={16} className="text-violet-500 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                        </div>
                        <button type="button" onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500 p-1">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between items-center">
              {step > 1 ? (
                <button 
                  type="button" onClick={prevStep}
                  className="flex items-center gap-2 px-6 py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <ChevronLeft size={20} /> Indietro
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setApplicantType(null)}
                  className="flex items-center gap-2 px-6 py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <ChevronLeft size={20} /> Cambia profilo
                </button>
              )}

              {step < 5 ? (
                <button 
                  type="submit"
                  className={`flex items-center gap-2 px-8 py-3 text-white font-medium rounded-xl transition-colors shadow-md ${
                    isCompanyFlow
                      ? 'bg-violet-600 hover:bg-violet-700 shadow-violet-200'
                      : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                  }`}
                >
                  Avanti <ChevronRight size={20} />
                </button>
              ) : (
                <button 
                  type="submit" disabled={isSubmitting}
                  className={`flex items-center gap-2 px-8 py-3 text-white font-medium rounded-xl transition-all ${isSubmitting ? 'bg-gray-400 opacity-70 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 shadow-md shadow-green-200'}`}
                >
                  {isSubmitting ? <Loader2 className="animate-spin w-5 h-5" /> : <CheckCircle size={20} />}
                  {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.4s ease-out forwards;
  }
`;
document.head.appendChild(style);